import { useMemo, useState } from "react";

export interface UseSearchOptions {
  /**
   * Canonicalises the query and each field before comparison — e.g. stripping
   * non-digits so a spaced/formatted card number still matches the stored PAN.
   * Applied to both sides, so matching stays symmetric. Pass a stable
   * reference (a module-level constant) to preserve memoisation.
   */
  normalize?: (value: string) => string;
}

/**
 * Text-search core shared by the issuer lists (cards, merchants, redeems).
 * Owns the query string and derives the filtered set by case-insensitively
 * matching the query against the given string keys of each item. Kept separate
 * from {@link SearchInput} so the markup stays declarative.
 *
 * Pass a stable `keys` reference (e.g. a module-level constant) so the memoised
 * result is not recomputed on every render.
 */
export function useSearch<T>(
  items: T[],
  keys: (keyof T)[],
  options?: UseSearchOptions,
) {
  const [query, setQuery] = useState("");
  const normalize = options?.normalize;

  const results = useMemo(() => {
    const raw = query.trim().toLowerCase();
    if (!raw) return items; // no query → everything
    const q = normalize ? normalize(raw) : raw;
    if (!q) return []; // query had content but normalised to nothing
    return items.filter((item) =>
      keys.some((key) => {
        const value = item[key];
        if (typeof value !== "string") return false;
        const field = normalize
          ? normalize(value.toLowerCase())
          : value.toLowerCase();
        return field.includes(q);
      }),
    );
  }, [items, query, keys, normalize]);

  return { query, setQuery, results };
}
